import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import styles from './TrackSpendingModal.module.css';
import Button from './Button';
import { addXP } from '../utils/questUtils';
import plaidService from '../services/plaidService';
import { useLives } from '../context/LivesContext';

interface TrackSpendingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (amount: number, category?: string, description?: string) => void;
}

const TrackSpendingModal: React.FC<TrackSpendingModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [amount, setAmount] = useState<string>('');
  const [category, setCategory] = useState<string>('Want');
  const [description, setDescription] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [xpGained, setXpGained] = useState<number>(0);
  const [animatedXp, setAnimatedXp] = useState<number>(0);
  const [heartsGained, setHeartsGained] = useState<number>(0);
  const [animatedHearts, setAnimatedHearts] = useState<number>(0);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [showPlaidPrompt, setShowPlaidPrompt] = useState<boolean>(false);
  const { addLives } = useLives();

  // Initialize Plaid link token
  useEffect(() => {
    const getLinkToken = async () => {
      try {
        const token = await plaidService.createLinkToken();
        setLinkToken(token);
      } catch (err) {
        console.error('Error getting link token:', err);
      }
    };

    if (isOpen) {
      getLinkToken();
    }
  }, [isOpen]);

  // Configure Plaid link
  const plaidConfig = linkToken ? {
    token: linkToken,
    onSuccess: async (public_token: string, metadata: any) => {
      try {
        console.log('Exchanging public token:', public_token);
        await plaidService.exchangePublicToken(public_token);
        handleClose();
      } catch (err) {
        console.error('Error exchanging token:', err);
      }
    },
    onExit: (err: any, metadata: any) => {
      if (err) {
        console.error('Error in Plaid Link:', err);
      }
    },
  } : null;

  const { open: openPlaid, ready: plaidReady } = usePlaidLink(plaidConfig || {
    token: null,
    onSuccess: () => {},
  });

  const handleClose = useCallback(() => {
    setAmount('');
    setCategory('Want');
    setDescription('');
    setShowSuccess(false);
    setXpGained(0);
    setAnimatedXp(0);
    setHeartsGained(0);
    setAnimatedHearts(0);
    setShowPlaidPrompt(false);
    onClose();
  }, [onClose]);

  const handleSubmit = () => {
    const numericAmount = parseFloat(amount);
    if (!isNaN(numericAmount) && numericAmount > 0) {
      const randomXp = Math.floor(Math.random() * 50) + 25; // Random XP between 25 and 75
      const randomHearts = Math.floor(Math.random() * 3) + 1; // Random hearts between 1 and 3
      setXpGained(randomXp);
      setHeartsGained(randomHearts);
      // Add XP to user's total
      addXP(randomXp);
      // Add hearts to user's total
      addLives(randomHearts);
      setShowSuccess(true);
    }
  };

  // XP and Hearts Animation Effect
  useEffect(() => {
    if (showSuccess && !showPlaidPrompt) {
      const animationDuration = 1000; // 1 second
      const xpAnimationStep = Math.max(1, Math.floor(xpGained / (animationDuration / 50)));
      let currentXp = 0;
      let currentHearts = 0;

      const interval = setInterval(() => {
        currentXp += xpAnimationStep;

        // Animate hearts (since it's small number, just increment by 1)
        if (currentHearts < heartsGained) {
          currentHearts++;
          setAnimatedHearts(currentHearts);
        }

        if (currentXp >= xpGained) {
          setAnimatedXp(xpGained);
          setAnimatedHearts(heartsGained);
          clearInterval(interval);

          // Wait a bit after animation before showing Plaid prompt
          setTimeout(() => {
            const numericAmount = parseFloat(amount);
            if (onComplete) {
              onComplete(numericAmount, category, description);
            }
            setShowPlaidPrompt(true);
          }, 1200);
        } else {
          setAnimatedXp(currentXp);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [showSuccess, xpGained, heartsGained, amount, category, description, onComplete, showPlaidPrompt]);

  if (!isOpen) {
    return null;
  }

  if (showSuccess) {
    return (
      <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>✓</div>
            <h2 className={styles.congratsTitle}>Expense Added!</h2>
            <div className={styles.xpContainer}>
              <span className={styles.xpAmount}>+{animatedXp} XP</span>
            </div>
            <div className={styles.xpContainer} style={{marginTop: '0.5rem'}}>
              <span className={styles.xpAmount}>+{animatedHearts} ❤️</span>
            </div>

            {showPlaidPrompt && (
              <div style={{marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0'}}>
                <p style={{color: '#555', fontSize: '1rem', fontWeight: 500, marginBottom: '1rem'}}>
                  Want this tracked automatically?
                </p>
                <button
                  onClick={() => {
                    console.log('Connect button clicked, linkToken:', linkToken, 'plaidReady:', plaidReady);
                    if (linkToken && plaidReady) {
                      openPlaid();
                    } else {
                      alert('Plaid is not ready yet. linkToken: ' + (linkToken ? 'yes' : 'no') + ', ready: ' + (plaidReady ? 'yes' : 'no'));
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.875rem 2rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 600,
                    width: '100%',
                    marginBottom: '0.75rem'
                  }}
                >
                  Connect Account
                </button>
                <button
                  onClick={handleClose}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  Skip for now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>How much was your last purchase?</h2>
        <div className={styles.inputContainer}>
          <div className={styles.inputWrapper}>
            <span className={styles.dollarSign}>$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={styles.amountInput}
              autoFocus
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={styles.categorySelect}
          >
            <option value="Want">Want</option>
            <option value="Need">Need</option>
            <option value="Savings">Savings</option>
          </select>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className={styles.descriptionInput}
            maxLength={50}
          />
        </div>
        <div className={styles.buttonContainer}>
          <Button 
            onClick={handleSubmit} 
            disabled={!amount || parseFloat(amount) <= 0}
            className={styles.doneButton}
          >
            Done
          </Button>
          <Button onClick={handleClose} className={styles.cancelButton}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrackSpendingModal;